function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { ButtonRoot, ContentContainer, SpinnerContainer, TextOverflow } from './styles';
import { Inset, Text, ThemingContext } from '@doordash/design-language';
import { Padding, Sizes, States, Types } from './constants';
import { colorForType, loadingSizeForButton, loadingStateForButton } from './utils';
import Loading from '@doordash/component-loading';
import PropTypes from 'prop-types';
import React from 'react';
import invariant from 'invariant';
import pickBy from 'lodash.pickby';

var Button = function Button(_ref) {
  var onClick = _ref.onClick,
      size = _ref.size,
      padding = _ref.padding,
      isInline = _ref.isInline,
      isDepressed = _ref.isDepressed,
      isDisabled = _ref.isDisabled,
      isToggleable = _ref.isToggleable,
      isToggled = _ref.isToggled,
      isRaised = _ref.isRaised,
      children = _ref.children,
      state = _ref.state,
      type = _ref.type,
      id = _ref.id,
      tag = _ref.tag,
      onAnimationEnd = _ref.onAnimationEnd,
      accessibilityLabel = _ref.accessibilityLabel,
      accessibilityRole = _ref.accessibilityRole,
      accessibilityDescribedBy = _ref.accessibilityDescribedBy,
      href = _ref.href,
      target = _ref.target,
      props = _objectWithoutProperties(_ref, ["onClick", "size", "padding", "isInline", "isDepressed", "isDisabled", "isToggleable", "isToggled", "isRaised", "children", "state", "type", "id", "tag", "onAnimationEnd", "accessibilityLabel", "accessibilityRole", "accessibilityDescribedBy", "href", "target"]);

  var theme = React.useContext(ThemingContext);
  invariant(type !== Types.FlatPrimary && type !== Types.FlatSecondary ? padding !== Padding.None : true, // prettier-ignore
  "\n      Do not use Button.Padding.None with any type that is not\n      Button.Types.FlatPrimary or Button.Types.FlatSecondary.\n    ");
  invariant(type === Types.FlatPrimary || type === Types.FlatSecondary ? !isToggleable : true, // prettier-ignore
  "\n      Do not set isToggleable to true when type is\n      Button.Types.FlatPrimary or Button.Types.FlatSecondary.\n    ");
  invariant(isRaised ? type === Types.Plain || type === Types.Primary || type === Types.PrimaryPill : true, // prettier-ignore
  "\n      You can only use isRaised with Plain, Primary\n      or PrimaryPill type buttons.\n    ");
  var labelStyles;

  switch (size) {
    case Button.Sizes.Small:
      labelStyles = Text.Styles.ButtonSmall;
      break;

    case Button.Sizes.Medium:
      labelStyles = Text.Styles.ButtonMedium;
      break;

    case Button.Sizes.Large:
      labelStyles = Text.Styles.ButtonLarge;
      break;

    case Button.Sizes.XLarge:
      labelStyles = Text.Styles.ButtonXLarge;
      break;

    default:
      labelStyles = Text.Styles.ButtonMedium;
  }

  var propsToForward = pickBy(props, function (value, key) {
    return key.startsWith('aria-') || key.startsWith('data-');
  });
  return React.createElement(ButtonRoot, _extends({
    themeColors: theme.Colors,
    themeBorderRadius: theme.BorderRadius.ButtonDefault,
    onClick: isDisabled || state !== States.Default ? null : onClick,
    id: id,
    tag: tag,
    size: size,
    type: "button",
    padding: padding,
    isRaised: isRaised,
    isInline: isInline,
    isDepressed: isDepressed,
    disabled: tag === 'button' ? isDisabled : undefined,
    kind: type,
    href: href,
    target: target,
    tabIndex: tag !== 'button' ? 0 : undefined,
    "aria-label": accessibilityLabel,
    "aria-pressed": isToggleable ? isToggled : undefined,
    "aria-disabled": tag !== 'button' ? isDisabled : undefined,
    "aria-describedby": accessibilityDescribedBy,
    isToggleable: isToggleable,
    isToggled: isToggled,
    role: // eslint-disable-next-line no-nested-ternary
    typeof accessibilityRole !== 'undefined' ? accessibilityRole : tag !== 'button' ? 'button' : undefined
  }, propsToForward), state !== States.Default && React.createElement(SpinnerContainer, {
    type: type
  }, React.createElement(Loading, {
    size: loadingSizeForButton(size),
    color: theme.Colors[colorForType(isToggleable, isToggled, type, isDisabled)],
    state: loadingStateForButton(state),
    onAnimationEnd: onAnimationEnd
  })), React.createElement(ContentContainer, {
    isHidden: state !== States.Default
  }, React.createElement(Inset, {
    size: padding === Padding.None ? Inset.Sizes.None : size,
    isSquished: true
  }, React.createElement(Text, {
    styles: labelStyles,
    color: colorForType(isToggleable, isToggled, type, isDisabled)
  }, React.createElement(TextOverflow, null, children)))));
};

Button.Padding = Padding;
Button.Sizes = Sizes;
Button.States = States;
Button.Types = Types;
Button.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  padding: PropTypes.oneOf(Object.values(Button.Padding)),
  size: PropTypes.oneOf(Object.values(Button.Sizes)),
  state: PropTypes.oneOf(Object.values(Button.States)),
  type: PropTypes.oneOf(Object.values(Button.Types)),
  isRaised: PropTypes.bool,
  isDepressed: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isInline: PropTypes.bool,
  isToggleable: PropTypes.bool,
  isToggled: PropTypes.bool,
  onAnimationEnd: PropTypes.func,
  accessibilityLabel: PropTypes.string,
  accessibilityRole: PropTypes.string,
  accessibilityDescribedBy: PropTypes.string,
  id: PropTypes.string,
  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  href: PropTypes.string,
  target: PropTypes.string
};
Button.defaultProps = {
  children: 'Click Me',
  size: Button.Sizes.Medium,
  type: Button.Types.Primary,
  state: Button.States.Default,
  padding: Button.Padding.Default,
  isRaised: false,
  isDepressed: false,
  isDisabled: false,
  isInline: false,
  isToggleable: false,
  isToggled: false,
  tag: 'button'
};
export default Button;